import React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Play, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useMembershipAccess } from "@/hooks/useMembershipAccess";

interface VideoMention {
  id: string;
  mentioned_profile_id: string;
  source_id: string;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
  approved_at?: string;
  rejected_at?: string;
}

interface Video {
  id: string;
  title: string;
  description?: string;
  author_name?: string;
}

interface VideoMentionModalProps {
  isOpen: boolean;
  onClose: () => void;
  mention: VideoMention | null;
  video: Video | null;
  loading?: boolean;
}

const VideoMentionModal: React.FC<VideoMentionModalProps> = ({
  isOpen,
  onClose,
  mention,
  video,
  loading = false
}) => {
  const navigate = useNavigate();
  const { hasAccessTo } = useMembershipAccess();

  if (!mention) return null;

  const handleWatchVideo = () => {
    // Check if user has video membership access
    if (hasAccessTo('videos') || hasAccessTo('premium')) {
      // Navigate to video page
      navigate('/videos/hoops_recaps');
    } else {
      // Navigate to purchase/upgrade page
      navigate('/videos/hoops_recaps');
    }
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Play className="h-5 w-5 text-primary" />
            You've been mentioned in a video
          </DialogTitle>
          <DialogDescription>
            Great news! You've been featured in a video. Click below to watch it now.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Video Details */}
          {video ? (
            <>
              {/* Video Title */}
              <div>
                <h3 className="font-semibold text-lg mb-2">{video.title}</h3>
                {video.author_name && (
                  <p className="text-sm text-gray-600 mb-3">
                    By {video.author_name}
                  </p>
                )}
              </div>

              {/* Video Description Preview */}
              {video.description && (
                <div className="border rounded-lg p-4 bg-gray-50">
                  <h4 className="font-medium mb-2">Video Description:</h4>
                  <div className="prose prose-sm max-w-none">
                    <p className="text-gray-700">
                      {video.description.length > 200 
                        ? video.description.substring(0, 200) + '...' 
                        : video.description}
                    </p>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="border rounded-lg p-4 bg-gray-50">
              <h4 className="font-medium mb-2">Video Information</h4>
              <p className="text-gray-600">
                The video details are not available at this time. This may be because the video has been removed or is still processing.
              </p>
            </div>
          )}

          {/* Information Note */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-800">
              <strong>Note:</strong> This video mention will automatically appear on your profile. 
              No approval is needed for video mentions.
            </p>
          </div>
        </div>

        <DialogFooter className="flex gap-2">
          <Button
            onClick={handleWatchVideo}
            className="bg-primary hover:bg-primary/90 flex items-center gap-2"
            disabled={loading}
          >
            <Play className="h-4 w-4" />
            Watch Video
            <ExternalLink className="h-4 w-4" />
          </Button>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default VideoMentionModal;